'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface TableScrollRegionProps {
  label: string;
  children: ReactNode;
  /**
   * Extra classes on the region -- e.g. `DataTable stickyHeader`'s bounded
   * fill (`min-h-0 flex-1 overflow-auto overscroll-none`), which turns this
   * into the one scroll box for both axes. Omitted -- unchanged rendering.
   */
  className?: string | undefined;
}

/**
 * Horizontally-scrollable table wrapper (page layout system, plan doc
 * docs/plans/2026-09-14-page-layout-system-plan.md §4.5 "Scroll, sticky,
 * clearance" / §4.6 "Table-family contract"). `overflow-x: auto`, labeled
 * `role="region"`, and a WCAG 2.1.1 scrollable-region-focusable `tabIndex` —
 * same wrapper contract `ReportTable.tsx`'s own `overflow-x-auto` region
 * already uses, factored out for `Table`/`DataTable` consumers that don't
 * go through `ReportTable`.
 *
 * Scroll-region focus (§4.5): `tabIndex=0` while the content actually
 * overflows horizontally; not a tab stop otherwise (a region with nothing to
 * scroll shouldn't eat a Tab press). If the region is focused while it
 * overflows and the overflow then goes away (e.g. a column is hidden via
 * `ColumnVisibilityMenu`, or the viewport widens), it keeps `tabIndex=0`
 * until blur — dropping `tabIndex` out from under a focused element would
 * silently move focus elsewhere.
 *
 * Overflow is (re)checked on mount and on resize of the region itself
 * (`ResizeObserver`, feature-detected — some test environments run with no
 * polyfill, e.g. `ReportTable`'s own `default-path-real-hook` test) and on
 * window resize as a fallback for environments without `ResizeObserver`.
 *
 * Overflow means EITHER axis. An unbounded region's scrollHeight equals its
 * clientHeight, so the vertical test is inert there; a bounded one (the locked
 * list header, where this box scrolls rows vertically) must stay reachable
 * even when nothing overflows horizontally. The content child is observed too:
 * in a fixed-height region, rows arriving (a page change) grow the content,
 * not the region, so observing the region alone would never re-check. The
 * child is captured once at mount; if it is later swapped for a different
 * element, the region observer and the window-resize fallback still re-check.
 */
export function TableScrollRegion({ label, children, className }: TableScrollRegionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () =>
      setOverflowing(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    check();

    window.addEventListener('resize', check);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(check);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    }
    return () => {
      window.removeEventListener('resize', check);
      observer?.disconnect();
    };
  }, []);

  const keepTabStop = overflowing || focused;

  return (
    <div
      ref={ref}
      role="region"
      aria-label={label}
      tabIndex={keepTabStop ? 0 : undefined}
      // React's onFocus/onBlur bubble, so only the region's OWN focus counts: a focused descendant (a drill
      // button in a cell) must not make the region a tab stop. It did, and Shift+Tab from the first such
      // control then targeted the region; the blur re-render dropped its tabIndex before the browser moved
      // focus there, so focus fell to <body> (P-9 F3).
      onFocus={(e) => {
        if (e.target === e.currentTarget) setFocused(true);
      }}
      onBlur={(e) => {
        if (e.target === e.currentTarget) setFocused(false);
      }}
      className={[
        // print:overflow-visible: a printed page cannot scroll, so a wide table
        // must not be clipped to the region's on-screen width.
        'overflow-x-auto print:overflow-visible focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring-focus',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
